'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

export interface PartnerDraft {
  /** Stable React key: server id for existing partners, temp uuid for new. */
  key: string;
  /** Server id; undefined means staged-only, not yet persisted. */
  id?: string;
  companyId: string;
  companyLabel: string;
  displayLabel: string;
  targetUrl: string;
  isActive: boolean;
  sortOrder: number;
}

interface Props {
  partners: PartnerDraft[];
  onChange: (partners: PartnerDraft[]) => void;
  t?: Record<string, string>;
}

type FormState = Omit<PartnerDraft, 'key' | 'id'>;

const empty: FormState = {
  companyId: '',
  companyLabel: '',
  displayLabel: '',
  targetUrl: '',
  isActive: true,
  sortOrder: 0,
};

function genKey() {
  return `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function PartnersSection({ partners, onChange, t }: Props) {
  const [formOpen, setFormOpen] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(empty);
  const [error, setError] = useState<string | null>(null);

  const openCreate = () => {
    setEditingKey(null);
    setForm(empty);
    setError(null);
    setFormOpen(true);
  };

  const openEdit = (p: PartnerDraft) => {
    setEditingKey(p.key);
    setForm({
      companyId: p.companyId,
      companyLabel: p.companyLabel,
      displayLabel: p.displayLabel,
      targetUrl: p.targetUrl,
      isActive: p.isActive,
      sortOrder: p.sortOrder,
    });
    setError(null);
    setFormOpen(true);
  };

  const validate = (): string | null => {
    if (!form.companyId.trim()) return t?.partnerRequiredCompanyId ?? 'Company ID is required';
    if (!/^[a-z0-9_\-]+$/.test(form.companyId)) return t?.partnerInvalidCompanyId ?? 'Invalid Company ID';
    if (!form.companyLabel.trim()) return t?.partnerRequiredCompanyLabel ?? 'Company name is required';
    if (!form.targetUrl.trim()) return t?.partnerRequiredTargetUrl ?? 'Check-in URL is required';
    try {
      new URL(form.targetUrl);
    } catch {
      return t?.partnerInvalidTargetUrl ?? 'Invalid URL';
    }
    return null;
  };

  const commit = () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    if (editingKey) {
      onChange(partners.map((p) => (p.key === editingKey ? { ...p, ...form } : p)));
    } else {
      onChange([...partners, { key: genKey(), ...form }]);
    }
    setFormOpen(false);
  };

  const remove = (key: string) => {
    if (!window.confirm(t?.partnerDeleteConfirm ?? 'Delete this partner?')) return;
    onChange(partners.filter((p) => p.key !== key));
  };

  return (
    <div>
      {partners.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">{t?.partnerColCompanyId ?? 'Company ID'}</th>
                <th className="px-3 py-2 text-left">{t?.partnerColCompanyLabel ?? 'Company'}</th>
                <th className="px-3 py-2 text-left">{t?.partnerColDisplayLabel ?? 'Display'}</th>
                <th className="px-3 py-2 text-left">{t?.partnerColTargetUrl ?? 'URL'}</th>
                <th className="px-3 py-2 text-center">{t?.partnerColActive ?? 'Active'}</th>
                <th className="px-3 py-2 text-center">{t?.partnerColActions ?? 'Actions'}</th>
              </tr>
            </thead>
            <tbody>
              {partners.map((p) => (
                <tr key={p.key} className="border-t">
                  <td className="px-3 py-2 font-mono">{p.companyId}</td>
                  <td className="px-3 py-2">{p.companyLabel}</td>
                  <td className="px-3 py-2 text-gray-500">{p.displayLabel || '—'}</td>
                  <td className="px-3 py-2 truncate max-w-[220px]">
                    <a href={p.targetUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                      {p.targetUrl}
                    </a>
                  </td>
                  <td className="px-3 py-2 text-center">{p.isActive ? '✓' : '—'}</td>
                  <td className="px-3 py-2 text-center whitespace-nowrap">
                    <button
                      type="button"
                      className="text-blue-600 hover:underline mr-3"
                      onClick={() => openEdit(p)}
                    >
                      {t?.partnerEdit ?? 'Edit'}
                    </button>
                    <button
                      type="button"
                      className="text-red-600 hover:underline"
                      onClick={() => remove(p.key)}
                    >
                      {t?.partnerRemove ?? 'Remove'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-sm text-gray-400 py-4 text-center">{t?.partnerEmpty ?? 'No partners yet'}</div>
      )}

      {!formOpen && (
        <div className="mt-3">
          <Button type="button" variant="outline" onClick={openCreate}>
            + {t?.partnerAdd ?? 'Add partner'}
          </Button>
        </div>
      )}

      {formOpen && (
        <div className="mt-4 p-4 border rounded-lg bg-gray-50">
          <div className="font-medium mb-3">{editingKey ? (t?.partnerEdit ?? 'Edit partner') : (t?.partnerAdd ?? 'Add partner')}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>{t?.partnerColCompanyId ?? 'Company ID'} *</Label>
              <Input
                value={form.companyId}
                onChange={(e) => setForm({ ...form, companyId: e.target.value })}
                pattern="[a-z0-9_\-]+"
                placeholder="aixc"
              />
            </div>
            <div>
              <Label>{t?.partnerColCompanyLabel ?? 'Company'} *</Label>
              <Input
                value={form.companyLabel}
                onChange={(e) => setForm({ ...form, companyLabel: e.target.value })}
                placeholder="AIxCrypto"
              />
            </div>
            <div>
              <Label>{t?.partnerColDisplayLabel ?? 'Display label'}</Label>
              <Input
                value={form.displayLabel}
                onChange={(e) => setForm({ ...form, displayLabel: e.target.value })}
              />
            </div>
            <div>
              <Label>{t?.partnerColActive ?? 'Active'}</Label>
              <div className="h-10 flex items-center">
                <Switch
                  checked={form.isActive}
                  onCheckedChange={(c) => setForm({ ...form, isActive: c })}
                />
              </div>
            </div>
            <div className="md:col-span-2">
              <Label>{t?.partnerColTargetUrl ?? 'Target URL'} *</Label>
              <Input
                type="url"
                value={form.targetUrl}
                onChange={(e) => setForm({ ...form, targetUrl: e.target.value })}
                placeholder="https://aixc.example.com/siteattendance/c/AIXC-HQ"
              />
            </div>
          </div>
          {error && (
            <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
              {error}
            </div>
          )}
          <div className="mt-4 flex gap-2">
            <Button type="button" onClick={commit}>
              {t?.partnerConfirm ?? 'Confirm'}
            </Button>
            <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>
              {t?.cancel ?? 'Cancel'}
            </Button>
          </div>
          <p className="text-xs text-gray-400 mt-3">
            {t?.partnerPendingHint ?? 'Changes are staged locally. Click Save at the bottom of the page to persist.'}
          </p>
        </div>
      )}
    </div>
  );
}
